import HttpError from '@/domains/common/typescript/HttpError';
import HydraAPI from '@/domains/common/services/hydraApi';
import userFriendlyMessageConverter from '@/domains/common/services/userFriendlyMessageConverter';
import { t } from '@/translations';
import transformDTOtoUserProfile from '@/domains/authentication/transformers/transformDTOtoUserProfile';
import type UserProfileInterface from '@/domains/authentication/typescript/UserProfileInterface';
import type UserProfileDTOInterface from '@/domains/authentication/typescript/UserProfileDTOInterface';
import type AdminOfficeUserEditDTOInterface from '@/domains/admin/typescript/AdminOfficeUserEditDTOInterface';

const API_URI = import.meta.env.VITE_API_URI;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAuthenticationToken = async (email: string, password: string): Promise<any> => {
    const response = await fetch(`${API_URI}/authentication_token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            password,
        }),
        credentials: 'include',
    });

    if (response.status === 401) {
        throw new HttpError(response.status, t('login.wrong_credentials'));
    }

    if (response.status === 403) {
        throw new HttpError(response.status, t('login.account_deactivated'));
    }

    if (response.status === 404) {
        throw new HttpError(response.status, t('api.404'));
    }

    const data = await response.json();

    if (data.status === 'error') {
        throw new HttpError(response.status, userFriendlyMessageConverter(data.message));
    }

    return data;
};

const getUserProfile = async (userOfficeId: string): Promise<UserProfileInterface> => {
    let url = `/office_users/${userOfficeId}`;
    if (userOfficeId.includes('office_users/')) {
        url = userOfficeId;
    }

    const userProfile = await HydraAPI.getJson<UserProfileDTOInterface>(url);

    return transformDTOtoUserProfile(userProfile);
};

const getUserProfileDTO = async (officeUserIri: string): Promise<UserProfileDTOInterface> => {
    return HydraAPI.getJson<UserProfileDTOInterface>(officeUserIri);
};

const postForgotPasword = async (email: string): Promise<Response> => {
    const response = await fetch(`${API_URI}/forgot_password/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
        }),
    });

    return response;
};

const postResetPassword = async (password: string, token: string): Promise<Response> => {
    const response = await fetch(`${API_URI}/forgot_password/${token}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            password,
        }),
    });

    return response;
};

const postCurrentOffice = async (
    userOfficeId: string,
    officeUser: Pick<AdminOfficeUserEditDTOInterface, 'user' | 'office'>,
): Promise<UserProfileDTOInterface> => {
    let url = `/office_users/${userOfficeId}/update`;
    if (userOfficeId.includes('office_users/')) {
        url = userOfficeId;
    }
    const body = JSON.stringify(officeUser);
    const data = await HydraAPI.postJson<UserProfileDTOInterface>(url, { body });

    return data;
};

export {
    getAuthenticationToken,
    getUserProfile,
    getUserProfileDTO,
    postForgotPasword,
    postResetPassword,
    postCurrentOffice,
};
